import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { PhysicsWorld } from '../src/physics/PhysicsWorld.js';
import { CharacterController } from '../src/physics/CharacterController.js';
import { getCitySceneManifest as getCityLayout } from '../src/cityLayout.js';
import {
  getHeroFloorAt as getHeroFloor,
  floorDefinitions,
  isInsideHeroBuilding
} from '../src/heroBuilding.js';
import { createVehicleMesh } from '../src/vehicles.js';

// ==========================================
// 1. Scene, Camera, Renderer Setup
// ==========================================
const container = document.getElementById('webgl-container');
const scene = new THREE.Scene();

// Bright, sunny Fortnite-style sky
scene.background = new THREE.Color(0x7dc2f5);
scene.fog = new THREE.FogExp2(0xaadcfc, 0.003);

const camera = new THREE.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.1, 700);
const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.35;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
container.appendChild(renderer.domElement);

// ==========================================
// 2. Lush Green Grass Ground & Sidewalks
// ==========================================
// Natural vibrant grass lawn terrain (like Fortnite ground)
const grassGeo = new THREE.PlaneGeometry(360, 360, 32, 32);
const grassMat = new THREE.MeshStandardMaterial({
  color: 0x56a63a, // Vibrant Fortnite grass green
  roughness: 0.85,
  metalness: 0.05
});
const grassMesh = new THREE.Mesh(grassGeo, grassMat);
grassMesh.rotation.x = -Math.PI / 2;
grassMesh.position.y = 0.0;
grassMesh.receiveShadow = true;
scene.add(grassMesh);

// Stylized Fluffy Clouds in Sky
function createCloud(x, y, z, scale = 1.0) {
  const cloudGroup = new THREE.Group();
  const cloudMat = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    roughness: 0.9,
    metalness: 0.1,
    flatShading: true
  });
  const puffs = [
    { x: 0, y: 0, z: 0, r: 8 },
    { x: 6, y: -1, z: 0, r: 6 },
    { x: -6, y: -1, z: 0, r: 6 },
    { x: 3, y: 3, z: 2, r: 5 },
    { x: -3, y: 2, z: -1, r: 5 }
  ];
  puffs.forEach(p => {
    const geo = new THREE.DodecahedronGeometry(p.r * scale, 1);
    const mesh = new THREE.Mesh(geo, cloudMat);
    mesh.position.set(p.x * scale, p.y * scale, p.z * scale);
    mesh.castShadow = false;
    cloudGroup.add(mesh);
  });
  cloudGroup.position.set(x, y, z);
  scene.add(cloudGroup);
}

// Add scenic clouds around town
createCloud(-60, 75, -50, 1.4);
createCloud(50, 85, -70, 1.8);
createCloud(80, 70, 40, 1.2);
createCloud(-70, 80, 60, 1.5);

// ==========================================
// 3. Sun, Ambient & Atmospheric Lighting
// ==========================================
// Warm Golden Sun Directional Light with deep soft shadows
const sunLight = new THREE.DirectionalLight(0xfffaee, 3.0);
sunLight.position.set(50, 95, 45);
sunLight.castShadow = true;
sunLight.shadow.mapSize.width = 2048;
sunLight.shadow.mapSize.height = 2048;
sunLight.shadow.camera.near = 1.0;
sunLight.shadow.camera.far = 280;
sunLight.shadow.camera.left = -65;
sunLight.shadow.camera.right = 65;
sunLight.shadow.camera.top = 65;
sunLight.shadow.camera.bottom = -65;
sunLight.shadow.bias = -0.0004;
sunLight.shadow.normalBias = 0.03;
scene.add(sunLight);
scene.add(sunLight.target);

// Sky & Ground Ambient Hemisphere Light
const hemiLight = new THREE.HemisphereLight(0xebf5ff, 0x486b3e, 1.3);
hemiLight.position.set(0, 50, 0);
scene.add(hemiLight);

// Sky Fill Bounce Light
const fillLight = new THREE.DirectionalLight(0x90c4f8, 0.8);
fillLight.position.set(-50, 70, -40);
scene.add(fillLight);

// ==========================================
// 4. Physics & Character Setup
// ==========================================
// Spawn directly on the street facing North down Main Street towards Clock Tower & Hero Building
const SPAWN_POS = { x: 0.0, y: 0.4, z: 22.0 };

const physicsWorld = new PhysicsWorld({ gravity: -18.0 });
const characterController = new CharacterController({
  physicsWorld,
  radius: 0.4,
  height: 1.8,
  stepHeight: 0.35,
  walkSpeed: 5.5,
  sprintSpeed: 9.0,
  jumpVelocity: 7.5,
  position: { ...SPAWN_POS }
});

// ==========================================
// 5. Player Avatar Mesh & Flashlight
// ==========================================
const playerGroup = new THREE.Group();
const avatarGroup = playerGroup;

// Stylized third-person player body
const torsoGeo = new THREE.CapsuleGeometry(0.35, 1.05, 8, 16);
const torsoMat = new THREE.MeshStandardMaterial({
  color: 0x00f3ff,
  roughness: 0.35,
  metalness: 0.4,
  emissive: 0x002233,
  emissiveIntensity: 0.3
});
const torsoMesh = new THREE.Mesh(torsoGeo, torsoMat);
torsoMesh.position.y = 0.9;
torsoMesh.castShadow = true;
torsoMesh.receiveShadow = true;
playerGroup.add(torsoMesh);

const headGeo = new THREE.SphereGeometry(0.22, 16, 16);
const headMat = new THREE.MeshStandardMaterial({
  color: 0x1f242d,
  roughness: 0.4,
  metalness: 0.6
});
const headMesh = new THREE.Mesh(headGeo, headMat);
headMesh.position.set(0, 1.55, 0);
headMesh.castShadow = true;
playerGroup.add(headMesh);

const visorGeo = new THREE.BoxGeometry(0.28, 0.12, 0.18);
const visorMat = new THREE.MeshStandardMaterial({
  color: 0xffffff,
  emissive: 0x00f3ff,
  emissiveIntensity: 1.2
});
const visorMesh = new THREE.Mesh(visorGeo, visorMat);
visorMesh.position.set(0, 1.55, 0.15);
playerGroup.add(visorMesh);

scene.add(playerGroup);

// Flashlight
const flashlight = new THREE.SpotLight(0xffffff, 5.0, 45, Math.PI / 6, 0.3, 1);
flashlight.castShadow = true;
flashlight.shadow.mapSize.width = 1024;
flashlight.shadow.mapSize.height = 1024;
flashlight.shadow.bias = -0.0005;
flashlight.visible = false;
scene.add(flashlight);
scene.add(flashlight.target);

// ==========================================
// 6. City, Buildings & Vehicles Pipeline
// ==========================================
const gltfLoader = new GLTFLoader();
const modelCache = new Map();

async function loadGLTFModel(url) {
  if (modelCache.has(url)) {
    return modelCache.get(url);
  }
  return new Promise((resolve) => {
    gltfLoader.load(
      url,
      (gltf) => {
        gltf.scene.traverse((child) => {
          if (child.isMesh) {
            child.castShadow = true;
            child.receiveShadow = true;
            if (child.material) {
              child.material.roughness = Math.max(child.material.roughness || 0.6, 0.35);
              child.material.metalness = Math.min(child.material.metalness || 0.1, 0.2);
            }
          }
        });
        modelCache.set(url, gltf.scene);
        resolve(gltf.scene);
      },
      undefined,
      (err) => {
        console.warn(`[GLTFLoader] Failed to load ${url}:`, err.message);
        resolve(null);
      }
    );
  });
}

async function buildCity() {
  const city = getCityLayout();

  // 1. Add all Physics Colliders to PhysicsWorld
  if (city.colliders) {
    for (const col of city.colliders) {
      physicsWorld.addCollider(col);
    }
  }

  // 2. Add all dynamic streetlights
  if (city.lights) {
    for (const l of city.lights) {
      const pointLight = new THREE.PointLight(l.color, l.intensity || 1.8, l.distance || 18, 1.5);
      pointLight.position.set(l.position[0], l.position[1], l.position[2]);
      pointLight.castShadow = !!l.castShadow;
      if (pointLight.castShadow) {
        pointLight.shadow.mapSize.width = 512;
        pointLight.shadow.mapSize.height = 512;
        pointLight.shadow.bias = -0.001;
      }
      scene.add(pointLight);

      const bulbGeo = new THREE.SphereGeometry(0.14, 8, 8);
      const bulbMat = new THREE.MeshBasicMaterial({ color: l.color });
      const bulb = new THREE.Mesh(bulbGeo, bulbMat);
      bulb.position.copy(pointLight.position);
      scene.add(bulb);
    }
  }

  // 3. Extract and preload unique GLB models
  const modelItems = (city.objects || city.models || city).filter(
    (item) => !!(item.modelPath || item.asset || item.model)
  );

  const uniqueUrls = [
    ...new Set(modelItems.map((item) => item.modelPath || item.asset || item.model))
  ];

  const progressFill = document.getElementById('progress-fill');
  const progressText = document.getElementById('progress-text');

  let loadedCount = 0;
  await Promise.all(
    uniqueUrls.map(async (url) => {
      await loadGLTFModel(url);
      loadedCount++;
      if (progressFill && progressText) {
        const pct = Math.round((loadedCount / uniqueUrls.length) * 100);
        progressFill.style.width = `${pct}%`;
        progressText.textContent = `Loading Fortnite Town assets (${loadedCount}/${uniqueUrls.length})...`;
      }
    })
  );

  // 4. Instantiate all GLB objects into scene
  for (const item of modelItems) {
    const url = item.modelPath || item.asset || item.model;
    const baseScene = modelCache.get(url);
    if (baseScene) {
      const instance = baseScene.clone(true);
      instance.position.set(item.position[0], item.position[1], item.position[2]);
      instance.rotation.set(item.rotation[0], item.rotation[1], item.rotation[2]);
      instance.scale.set(item.scale[0], item.scale[1], item.scale[2]);
      scene.add(instance);
    }
  }

  // 5. Instantiate 3D Vehicles (Pickup Truck, Sedan, Van)
  const vehicleItems = (city.objects || city.models || city).filter(
    (item) => item.type === 'vehicle' && item.vehicleType
  );
  for (const v of vehicleItems) {
    const vMesh = createVehicleMesh(v.vehicleType);
    vMesh.position.set(v.position[0], v.position[1], v.position[2]);
    vMesh.rotation.set(v.rotation[0], v.rotation[1], v.rotation[2]);
    vMesh.scale.set(v.scale[0], v.scale[1], v.scale[2]);
    scene.add(vMesh);
  }

  // 6. Fade out and remove loading overlay
  const loadingOverlay = document.getElementById('loading-overlay');
  if (loadingOverlay) {
    loadingOverlay.classList.add('fade-out');
    setTimeout(() => {
      loadingOverlay.style.display = 'none';
    }, 400);
  }
}

buildCity();

// ==========================================
// 7. Camera, Controls & Pointer Lock
// ==========================================
let isFirstPerson = false;
let isFlyMode = false;
let isLocked = false;
let pitch = 0;
let yaw = 0; // Facing North down Main Street
let walkCycle = 0;

const keys = {
  forward: false,
  backward: false,
  left: false,
  right: false,
  jump: false,
  sprint: false
};

const crosshair = document.getElementById('crosshair');

function requestLock() {
  document.body.requestPointerLock();
}

document.addEventListener('pointerlockchange', () => {
  isLocked = document.pointerLockElement === document.body;
  if (crosshair) {
    crosshair.style.display = isLocked && isFirstPerson ? 'block' : 'none';
  }
});

window.addEventListener('click', (e) => {
  if (e.target.tagName === 'BUTTON' || e.target.closest('button')) return;
  if (!isLocked) requestLock();
});

// Mouse Look handler
window.addEventListener('mousemove', (e) => {
  if (!isLocked) return;
  const sensitivity = 0.0022;
  yaw -= e.movementX * sensitivity;
  pitch -= e.movementY * sensitivity;

  const maxPitch = 1.48;
  pitch = Math.max(-maxPitch, Math.min(maxPitch, pitch));
});

// Keyboard Input handler
window.addEventListener('keydown', (e) => {
  switch (e.code) {
    case 'KeyW':
    case 'ArrowUp':
      keys.forward = true;
      break;
    case 'KeyS':
    case 'ArrowDown':
      keys.backward = true;
      break;
    case 'KeyA':
    case 'ArrowLeft':
      keys.left = true;
      break;
    case 'KeyD':
    case 'ArrowRight':
      keys.right = true;
      break;
    case 'Space':
      keys.jump = true;
      break;
    case 'ShiftLeft':
    case 'ShiftRight':
      keys.sprint = true;
      break;
    case 'KeyC':
      toggleCameraView();
      break;
    case 'KeyF':
      toggleFlashlight();
      break;
    case 'KeyV':
      toggleFlyMode();
      break;
    case 'KeyR':
      respawnPlayer();
      break;
  }
});

window.addEventListener('keyup', (e) => {
  switch (e.code) {
    case 'KeyW':
    case 'ArrowUp':
      keys.forward = false;
      break;
    case 'KeyS':
    case 'ArrowDown':
      keys.backward = false;
      break;
    case 'KeyA':
    case 'ArrowLeft':
      keys.left = false;
      break;
    case 'KeyD':
    case 'ArrowRight':
      keys.right = false;
      break;
    case 'Space':
      keys.jump = false;
      break;
    case 'ShiftLeft':
    case 'ShiftRight':
      keys.sprint = false;
      break;
  }
});

function toggleCameraView() {
  isFirstPerson = !isFirstPerson;
  avatarMeshVisible(!isFirstPerson);
  const statCam = document.getElementById('stat-cam');
  if (statCam) statCam.textContent = isFirstPerson ? 'First-Person' : 'Third-Person';
  if (isLocked && crosshair) {
    crosshair.style.display = isFirstPerson ? 'block' : 'none';
  }
}

function avatarMeshVisible(visible) {
  playerGroup.visible = visible;
}

function toggleFlashlight() {
  flashlight.visible = !flashlight.visible;
  const statLight = document.getElementById('stat-light');
  if (statLight) {
    statLight.textContent = flashlight.visible ? 'ON' : 'OFF';
    statLight.style.color = flashlight.visible ? '#3fb950' : '#8b949e';
  }
}

function toggleFlyMode() {
  isFlyMode = !isFlyMode;
}

function respawnPlayer() {
  characterController.teleport(SPAWN_POS.x, SPAAWN_Y_CLEAN, SPAWN_POS.z);
  yaw = 0;
  pitch = 0;
}
const SPAAWN_Y_CLEAN = 0.5;

document.getElementById('btn-cam')?.addEventListener('click', toggleCameraView);
document.getElementById('btn-respawn')?.addEventListener('click', respawnPlayer);

// ==========================================
// 8. HUD Telemetry & Floor Detection
// ==========================================
let frameCount = 0;
let lastFpsTime = performance.now();
let currentFps = 60.0;
let currentFloorId = null;

const statFps = document.getElementById('stat-fps');
const statPos = document.getElementById('stat-pos');
const statVel = document.getElementById('stat-vel');
const statGround = document.getElementById('stat-ground');
const floorBanner = document.getElementById('floor-banner');
const floorTitle = document.getElementById('floor-title');
const floorSub = document.getElementById('floor-sub');

function updateHUD(dt) {
  frameCount++;
  const now = performance.now();
  if (now - lastFpsTime >= 250) {
    currentFps = (frameCount * 1000) / (now - lastFpsTime);
    frameCount = 0;
    lastFpsTime = now;

    if (statFps) {
      statFps.textContent = currentFps.toFixed(1);
      statFps.className = `stat-val ${currentFps >= 50 ? 'fps-good' : currentFps >= 30 ? 'fps-mid' : 'fps-low'}`;
    }
  }

  const p = characterController.position;
  const v = characterController.velocity;
  const speed = Math.sqrt(v.x * v.x + v.z * v.z);

  if (statPos) statPos.textContent = `${p.x.toFixed(1)}, ${p.y.toFixed(1)}, ${p.z.toFixed(1)}`;
  if (statVel) statVel.textContent = `${speed.toFixed(1)} m/s`;
  if (statGround) {
    statGround.textContent = characterController.isGrounded ? 'YES' : 'NO';
    statGround.style.color = characterController.isGrounded ? '#3fb950' : '#d29922';
  }

  // 4-Story Hero Building Floor Detection
  const inside = isInsideHeroBuilding(p.x, p.z);
  if (inside && p.y >= -0.5 && p.y <= 16.0) {
    const floorIndex = getHeroFloor(p.y);
    const activeFloor = floorDefinitions[floorIndex - 1];
    if (activeFloor) {
      if (currentFloorId !== activeFloor.floor) {
        currentFloorId = activeFloor.floor;
        if (floorTitle) floorTitle.textContent = `Floor ${activeFloor.floor}: ${activeFloor.name}`;
        if (floorSub) floorSub.textContent = activeFloor.description;
        floorBanner?.classList.add('active');
      }
    }
  } else {
    if (currentFloorId !== null) {
      currentFloorId = null;
      floorBanner?.classList.remove('active');
    }
  }
}

// ==========================================
// 9. Main 60 FPS Render & Simulation Loop
// ==========================================
const clock = new THREE.Clock();

function animate() {
  requestAnimationFrame(animate);

  const delta = Math.min(clock.getDelta(), 0.1);

  // Compute character movement input relative to camera yaw
  let moveX = 0;
  let moveZ = 0;
  if (keys.forward) moveZ += 1;
  if (keys.backward) moveZ -= 1;
  if (keys.left) moveX -= 1;
  if (keys.right) moveX += 1;

  if (isFlyMode) {
    const flySpeed = keys.sprint ? 24 : 10;
    const forward = new THREE.Vector3(
      -Math.sin(yaw) * Math.cos(pitch),
      Math.sin(pitch),
      -Math.cos(yaw) * Math.cos(pitch)
    );
    const right = new THREE.Vector3(Math.cos(yaw), 0, -Math.sin(yaw));

    const p = characterController.position;
    if (keys.forward) {
      p.x += forward.x * flySpeed * delta;
      p.y += forward.y * flySpeed * delta;
      p.z += forward.z * flySpeed * delta;
    }
    if (keys.backward) {
      p.x -= forward.x * flySpeed * delta;
      p.y -= forward.y * flySpeed * delta;
      p.z -= forward.z * flySpeed * delta;
    }
    if (keys.right) {
      p.x += right.x * flySpeed * delta;
      p.z += right.z * flySpeed * delta;
    }
    if (keys.left) {
      p.x -= right.x * flySpeed * delta;
      p.z -= right.z * flySpeed * delta;
    }
    if (keys.jump) {
      p.y += flySpeed * delta;
    }
    characterController.velocity.set(0, 0, 0);
  } else {
    characterController.setInput({
      moveX,
      moveZ,
      jump: keys.jump,
      sprint: keys.sprint,
      yaw
    });

    physicsWorld.step(delta);
  }

  const charPos = characterController.position;

  // Update Avatar visual position and rotation
  playerGroup.position.set(charPos.x, charPos.y, charPos.z);
  playerGroup.rotation.y = yaw;

  // Walk animation tilt / bob
  const isMoving = (moveX !== 0 || moveZ !== 0) && characterController.isGrounded;
  if (isMoving) {
    walkCycle += delta * (keys.sprint ? 16 : 10);
    torsoMesh.position.y = 0.9 + Math.sin(walkCycle) * 0.04;
    headMesh.position.y = 1.55 + Math.sin(walkCycle) * 0.04;
    visorMesh.position.y = 1.55 + Math.sin(walkCycle) * 0.04;
    playerGroup.rotation.z = Math.sin(walkCycle * 0.5) * 0.03;
  } else {
    torsoMesh.position.y = 0.9;
    headMesh.position.y = 1.55;
    visorMesh.position.y = 1.55;
    playerGroup.rotation.z = 0;
  }

  // Camera Positioning & Rig
  const eyeHeight = 1.65;
  if (isFirstPerson) {
    camera.position.set(charPos.x, charPos.y + eyeHeight, charPos.z);
    const lookTarget = new THREE.Vector3(
      charPos.x - Math.sin(yaw) * Math.cos(pitch) * 10,
      charPos.y + eyeHeight + Math.sin(pitch) * 10,
      charPos.z - Math.cos(yaw) * Math.cos(pitch) * 10
    );
    camera.lookAt(lookTarget);
  } else {
    // Third-Person: Camera orbits behind player looking forward
    const armDistance = 3.8;
    const armHeight = 1.9;
    const camX = charPos.x + Math.sin(yaw) * Math.cos(pitch) * armDistance;
    const camY = charPos.y + armHeight + Math.sin(pitch) * armDistance * 0.8;
    const camZ = charPos.z + Math.cos(yaw) * Math.cos(pitch) * armDistance;

    camera.position.set(camX, Math.max(camY, 0.4), camZ);

    const focusPoint = new THREE.Vector3(charPos.x, charPos.y + 1.4, charPos.z);
    camera.lookAt(focusPoint);
  }

  // Flashlight update
  if (flashlight.visible) {
    flashlight.position.copy(camera.position);
    const forwardVec = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
    flashlight.target.position.copy(camera.position).addScaledVector(forwardVec, 15);
  }

  // Sun follow character to maintain crisp shadow coverage
  sunLight.position.set(charPos.x + 50, 95, charPos.z + 45);
  sunLight.target.position.set(charPos.x, charPos.y, charPos.z);

  // Update HUD
  updateHUD(delta);

  // Render WebGL frame
  renderer.render(scene, camera);
}

// Start render loop
animate();

// Resize Handler
window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
});
